import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { observable, action, runInAction, makeObservable, computed } from "mobx";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { API } from "General/API";
import { DocumentsBreadCrumbConfig } from "Pages/Project/Documents/Components/DocumentsBreadcrumbs";
import { compareValues } from "utils/defaultSort";
import { SortDirectionType } from "react-virtualized";
import { download } from "utils/download";
import { uniq } from "lodash";
import { API_UPLOADITEM_REPLACE } from "General/APIEnums";
import { TENDEROFFERSTATUS } from "Pages/Guest/GuestBidderTrade/Components/TenderOfferStatus";
import { actionProgressStore } from "Components/ActionProgress/ActionProgressStore";

export interface TenderCallDocumentsParams {
  includeFiles?: API_TENDER_DOCUMENTS_INCLUDEFILES;
}

export interface CFTDocument extends API_CftFolder, API_CftFile {}
/**
 * We have bidder guest page and user page for tender details. Both have their own documents tab.
 * Both use same request and response objects, but different endpoints. urlsConfig is used to initialize MobX store
 * for particular mode - guest or project user.
 */
interface StoreUrlConfig {
  root: (callId: number | string) => string;
  folderById: (callId: number | string, folderId: number | string) => string;
  createFolder: (callId: number | string, folderId: number | string) => string;
  downloadFiles: (callId: number | string) => string;
  deleteFiles: (callId: number | string) => string;
}

const urlsConfig: { [key in "user" | "guest"]: StoreUrlConfig } = {
  user: {
    root: (callId) => API_URL.TENDER_DOCUMENTS.getTenderRoot(projectsDataStore.projectId, callId),
    folderById: (callId, folderId) =>
      API_URL.TENDER_DOCUMENTS.getTenderFolder(projectsDataStore.projectId, callId, folderId),
    createFolder: (callId, parentId) =>
      API_URL.TENDER_DOCUMENTS.createTenderFolder(projectsDataStore.projectId, callId, parentId),
    downloadFiles: (callId) => API_URL.TENDER.getFileDownload(projectsDataStore.projectId, callId),
    deleteFiles: (callId) => API_URL.TENDER_DOCUMENTS.deleteFiles_2(projectsDataStore.projectId, callId),
  },
  guest: {
    root: (callId) => API_URL.TENDER_GUEST.getDocuments(callId),
    folderById: (callId, folderId) => API_URL.TENDER_GUEST.getDocumentsByFolder(callId, folderId),
    createFolder: (_callId, _parentId) => "NOT_DEFINED",
    downloadFiles: (callId) => API_URL.TENDER_GUEST.getFileDownload_1(callId),
    deleteFiles: (_callId) => "TODO:",
  },
};

export class TenderCallDocumentsStore extends CoreStore {
  offerStatus: TENDEROFFERSTATUS;
  currentFolderId: number;
  canAddFiles = false;
  canMoveFiles = false;
  accessLevel: API_CFTFOLDER_ACCESSLEVEL = undefined;
  canCreateSubDirs = false;
  data: CFTDocument[] = [];
  breadCrumbs: DocumentsBreadCrumbConfig[] = [];
  sortBy: keyof CFTDocument = "name";
  sortDirection: SortDirectionType = "ASC";
  showDeleted = false;
  callId: number | string;
  uploadPackage: API_UploadPackage = null;
  private urlsConfig: StoreUrlConfig;
  constructor(urlConfig: StoreUrlConfig) {
    super();

    makeObservable(this, {
      offerStatus: observable,
      currentFolderId: observable,
      uploadPackage: observable,
      canAddFiles: observable,
      canMoveFiles: observable,
      accessLevel: observable,
      canCreateSubDirs: observable,
      data: observable,
      breadCrumbs: observable,
      sortBy: observable,
      sortDirection: observable,
      showDeleted: observable,
      callId: observable,
      clear: action,
      reFetch: action.bound,
      goBackToFolder: action.bound,
      addChildFolder: action.bound,
      createFolder: action,
      downloadFiles: action,
      sort: action.bound,
      toggleShowDeleted: action.bound,
      deleteFiles: action.bound,
      moveFile: action.bound,
      init: action.bound,
      fetch: action.bound,
      uploadProjectFileToTemp: action.bound,
      removeFromUploadPackage: action,
      submitTempPackage: action.bound,
      setUploadPackage: action.bound,
      makeOffer: action.bound,
      updateOffer: action.bound,
      getOffer: action.bound,
      uploadPackageFiles: computed,
      existingPackageFiles: computed,
    });

    this.urlsConfig = urlConfig;
  }

  url(id: number | string) {
    return this.urlsConfig.folderById(this.callId, id);
  }

  clear() {
    this.uploadPackage = null;
    this.data = [];
    this.breadCrumbs = [];
    this.currentFolderId = undefined;
    this.canAddFiles = false;
    this.canMoveFiles = false;
    this.accessLevel = undefined;
    this.canCreateSubDirs = false;
    this.callId = undefined;
    this.sortBy = "name";
    this.sortDirection = "ASC";
    this.showDeleted = false;
    this.offerStatus = undefined;
  }

  // used to fetch root folder
  @handleStoreState()
  async init(callId: number | string) {
    try {
      const response: API_CftFolder = await API.getData(this.urlsConfig.root(callId));
      runInAction(() => {
        this.callId = callId;
        this.processResponse(response);
      });
    } catch (error) {
      throw error;
    }
  }

  // used to fetch child folders
  @handleStoreState()
  async fetch(folderId: string) {
    try {
      folderId = folderId?.replace("F", "");
      const params: TenderCallDocumentsParams = {};
      if (this.showDeleted) {
        params.includeFiles = "ALL";
      }

      const response: API_CftFolder = await API.getData(this.url(folderId), params);
      runInAction(() => {
        this.processResponse(response);
      });
    } catch (error) {
      throw error;
    }
  }

  get uploadPackageFiles() {
    return this.uploadPackage?.files;
  }

  get existingPackageFiles() {
    return this.uploadPackage?.alreadyExisting || [];
  }

  @handleStoreState()
  async uploadProjectFileToTemp(addedFileIds: number[], folderId?: number) {
    try {
      const base = this.uploadPackage?.files.concat(this.uploadPackage?.alreadyExisting) || [];
      const ids = uniq(base.map((f) => f.id).concat(addedFileIds));

      const formData: API_UploadRequest = {
        ids: ids.filter(Boolean),
        uploadPackage: {
          // hardcoded as long as we dont have a user handling for the situation
          globalReplace: API_UPLOADITEM_REPLACE.KEEP_EXISTING,
        },
      };
      const response: API_UploadPackage = await API.newData(
        API_URL.TENDER_DOCUMENTS.addProjectFile(
          projectsDataStore.projectId,
          this.callId,
          folderId || this.currentFolderId
        ),
        formData
      );
      if (response) {
        runInAction(() => {
          this.setUploadPackage(response);
        });
      }
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  removeFromUploadPackage(fileId: number, type: "new" | "existing") {
    const packageFiles = type === "new" ? this.uploadPackage.files : this.uploadPackage.alreadyExisting;
    const idx = packageFiles.findIndex((f) => f.id === fileId);
    if (idx > -1) {
      packageFiles.splice(idx, 1);
    }
  }

  @handleStoreState()
  async submitTempPackage(packageObj?: API_UploadPackage, folderId?: number) {
    const formData: API_UploadPackage = packageObj || this.uploadPackage;
    try {
      await API.newData(
        API_URL.TENDER_DOCUMENTS.submitUpload(
          projectsDataStore.projectId,
          this.callId,
          folderId || this.currentFolderId
        ),
        formData
      );
      runInAction(() => {
        this.uploadPackage = null;
      });
    } catch (error) {
      throw error;
    }
  }

  reFetch() {
    return this.fetch(this.currentFolderId?.toString());
  }

  processResponse(response: API_CftFolder) {
    this.currentFolderId = response.id;
    this.accessLevel = response.accessLevel;
    this.canAddFiles = response.accessLevel === "FULL";
    this.canMoveFiles = response.moveFiles;
    this.canCreateSubDirs = response.createSubDirs && response.accessLevel === "FULL";
    this.addChildFolder(response);
    // NOTE: because folder and file id's are crossing - we add "F" to folderId.
    // Make sure to remove it when fetch folder by id...
    this.data = [].concat(
      response.subFolders.map((item) => ({ ...item, id: "F" + item.id })),
      response.files
    );
  }

  // BREADCRUMBS START
  goBackToFolder(folderId: number) {
    const index = this.breadCrumbs.findIndex((item) => item.folderId === folderId);
    if (index !== -1) {
      this.breadCrumbs.splice(index + 1);
    }
    this.fetch(folderId.toString());
  }

  addChildFolder(folder: API_CftFolder) {
    /// avoid adding one folder two times
    if (this.breadCrumbs.findIndex((item) => item.folderId === folder.id) === -1) {
      this.breadCrumbs.push({ title: folder.name, folderId: folder.id });
    }
  }
  // BREADCRUMBS END

  async createFolder(parentFolderId: string, foldername: string) {
    try {
      parentFolderId = parentFolderId.replace("F", "");
      const url = API_URL.TENDER_DOCUMENTS.createTenderFolder(projectsDataStore.projectId, this.callId, parentFolderId);
      await API.newData(url, { stringValue: foldername });
      runInAction(() => {
        this.fetch(parentFolderId);
      });
    } catch (error) {
      throw error;
    }
  }

  async downloadFiles(ids: number[]) {
    const downloadProgressId = actionProgressStore.init();
    try {
      const cancelToken = actionProgressStore.getCancelToken(downloadProgressId);
      const file = await API.getBlobByPost(this.urlsConfig.downloadFiles(this.callId), ids, null, {
        onDownloadProgress: (evt) => {
          actionProgressStore.setProgress(downloadProgressId, evt);
        },
        cancelToken,
      });
      download(file, file.name);
    } catch (error) {
      throw error;
    } finally {
      actionProgressStore.dismiss(downloadProgressId);
    }
  }

  sort(prop: keyof CFTDocument, direction: "ASC" | "DESC") {
    const array = this.data.slice();
    array.sort(compareValues(prop, direction));
    // sort have folders always on top
    array.sort((a, b) => {
      const aIsFile = Object.prototype.hasOwnProperty.call(a, "fileType");
      const bIsFile = Object.prototype.hasOwnProperty.call(b, "fileType");
      if (aIsFile) {
        return 1;
      } else if (bIsFile) {
        return -1;
      }
      return 0;
    });

    this.data = array;
    this.sortBy = prop;
    this.sortDirection = direction;
  }

  toggleShowDeleted() {
    this.showDeleted = !this.showDeleted;
    this.reFetch();
  }

  async deleteFiles(ids: number[]) {
    try {
      await API.deleteData(this.urlsConfig.deleteFiles(this.callId), null, {
        body: ids,
      });
      this.reFetch();
    } catch (error) {
      throw error;
    }
  }

  async moveFile(files: number[], targetFolderId: number) {
    try {
      const formData: API_CftMoveRequest = {
        targetFolder: targetFolderId,
        files,
      };
      await API.newData(API_URL.TENDER_DOCUMENTS.move_1(projectsDataStore.projectId, this.callId), formData);
      this.reFetch();
    } catch (error) {
      throw error;
    }
  }

  setUploadPackage(data: API_UploadPackage) {
    this.uploadPackage = { ...data };
  }

  async makeOffer(id: number | string, price: number | string, discount: number | string) {
    try {
      await API.newData(API_URL.CFT_OFFER.makeOffer(id), { price, discount: discount ? discount : 0 });
    } catch (error) {
      throw error;
    }
  }
  async updateOffer(id: number | string, price: number | string, discount: number | string) {
    try {
      await API.editData(API_URL.CFT_OFFER.updateOffer(id), { price, discount: discount ? discount : 0 });
    } catch (error) {
      throw error;
    }
  }

  async getOffer(callId: string | number) {
    try {
      return await API.getData(API_URL.CFT_OFFER.getOffer(callId));
    } catch (error) {
      throw error;
    }
  }

  async getOfferStatus(callId: string | number) {
    try {
      const response = await API.getData<TENDEROFFERSTATUS>(API_URL.CFT_OFFER.getOfferStatus(callId));
      runInAction(() => {
        this.offerStatus = response;
      });
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export const tenderCallDocumentsStore = new TenderCallDocumentsStore(urlsConfig.user);
export const guestCallDocumentsStore = new TenderCallDocumentsStore(urlsConfig.guest);
